import { useCallback, useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useTranslation } from "react-i18next";
import { FiCopy, FiEdit2, FiTrash2, FiX } from "react-icons/fi";
import { TbNote } from "react-icons/tb";
import { createPortal } from "react-dom";
import { CockpitHeaderRight } from "@dd-report-components/report/Styles";
import {
  AMBER_INK,
  BORDER_DASHED,
  BORDER_SUBTLE,
  BORDER_TINT,
  FONT_REPORT,
  INK,
  TEXT_HEADING,
  TEXT_PLACEHOLDER,
  SEMAPHORE,
  TINT_AMBER,
  TINT_RED,
  WHITE,
} from "@dd-report-components/tokens";
import { REPORT_NOTE_MAX_LENGTH, type DueDiligenceReportNote } from "../../../../Common/Types-DTOs/DueDiligence";
import { formatAbbreviatedDate } from "../../../../Common/Utils/dateUtils";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`;

// ── Waking the marks ─────────────────────────────────────────────
// The mark used to surface when the pointer entered the header it sits in,
// which made it findable only by a reader who already suspected it was there.
// Now any movement of the pointer over the report wakes every mark on screen,
// and a second and a half of a still cursor fades them back out, so a reader
// who has stopped to read is not left with a control floating over the text.

const MARKER_SLEEP_MS = 1500;

export const useMarkerWake = () => {
  const [awake, setAwake] = useState(false);
  const sleepTimer = useRef<number>();

  useEffect(() => () => window.clearTimeout(sleepTimer.current), []);

  const wake = useCallback(() => {
    setAwake(true);
    window.clearTimeout(sleepTimer.current);
    sleepTimer.current = window.setTimeout(() => setAwake(false), MARKER_SLEEP_MS);
  }, []);

  return { markersAwake: awake, wakeMarkers: wake };
};

// ── The marker ───────────────────────────────────────────────────
// A post-it in the report's own amber, sat at the right end of the section
// header row — the one stretch of that row the design leaves empty. It lives
// inside the content column on purpose: the report is a 1100px wrapper next
// to a 240px sidebar, so on anything narrower than a large desktop there is
// no outer margin for it to float in.

const Marker = styled.button<{ $hasNotes: boolean; $awake: boolean }>`
  flex-shrink: 0;
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
  width: ${(p) => (p.$hasNotes ? "auto" : "1.625rem")};
  min-width: 1.625rem;
  height: 1.625rem;
  padding: ${(p) => (p.$hasNotes ? "0 0.4rem" : "0")};
  font-family: ${FONT_REPORT};
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  border-radius: 0.3rem;
  /* The corner a post-it is peeled from. */
  border-bottom-right-radius: 0.55rem;
  transition: opacity 0.35s ease, background 0.16s ease, border-color 0.16s ease;

  background: ${(p) => (p.$hasNotes ? TINT_AMBER : "transparent")};
  border: 1px ${(p) => (p.$hasNotes ? "solid" : "dashed")}
    ${(p) => (p.$hasNotes ? BORDER_TINT.amber : BORDER_DASHED)};
  color: ${(p) => (p.$hasNotes ? AMBER_INK : INK.faint)};

  /* A section that already holds notes keeps its mark on permanently — the
     count is information, not a control. An empty one carries none until the
     pointer moves: thirteen dashed squares down the report would read as an
     unfinished form. */
  opacity: ${(p) => (p.$hasNotes || p.$awake ? 1 : 0)};
  &:focus-visible {
    opacity: 1;
  }

  /* The cockpit's right column is a right-aligned block, not the header row's
     flex line: the mark stacks under the date instead of sharing a baseline. */
  ${CockpitHeaderRight} & {
    margin-top: 0.5rem;
  }

  /* The pointer resting on the mark itself is not an idle cursor: it must not
     fade out from under the hand that is reaching for it. */
  &:hover {
    opacity: 1;
    background: ${TINT_AMBER};
    border-color: ${BORDER_TINT.amber};
    color: ${AMBER_INK};
  }
`;

interface MarkerProps {
  count: number;
  awake: boolean;
  onClick: () => void;
}

export const SectionNotesMarker = ({ count, awake, onClick }: MarkerProps) => {
  const { t } = useTranslation();
  const label = count
    ? t("dueDiligence.finalReport.notes.markerWithCount", { count })
    : t("dueDiligence.finalReport.notes.markerEmpty");

  return (
    <Marker $hasNotes={count > 0} $awake={awake} onClick={onClick} title={label} aria-label={label}>
      <TbNote size={13} />
      {count > 0 && count}
    </Marker>
  );
};

// ── The context menu ─────────────────────────────────────────────
// Right-clicking anywhere in the report offers to annotate the section on
// screen, the way a word processor does. It replaces the browser's own menu
// inside the report body only — the rest of the app keeps it. Selecting text
// pops the same sheet unprompted, Notion-style, and then it also offers to
// copy what is under the selection.

const MenuSheet = styled.div`
  position: fixed;
  z-index: 1400;
  min-width: 11rem;
  padding: 0.3rem;
  background: ${WHITE};
  border: 1px solid ${BORDER_SUBTLE};
  border-radius: 0.5rem;
  box-shadow: 0 12px 32px rgba(20, 50, 59, 0.18);
  font-family: ${FONT_REPORT};
  animation: ${fadeIn} 0.1s ease;
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem 0.625rem;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  text-align: left;
  color: ${TEXT_HEADING};
  background: transparent;
  border: none;
  border-radius: 0.35rem;
  cursor: pointer;

  &:hover {
    background: ${TINT_AMBER};
    color: ${AMBER_INK};
  }
`;

export interface NotesMenuAnchor {
  x: number;
  y: number;
  sectionNumber: number;
  // Present when the menu was raised over a selection: what the user picked.
  selectedText?: string;
}

interface MenuProps {
  anchor: NotesMenuAnchor;
  onAddNote: () => void;
  onClose: () => void;
}

// Menu width, and the height of a single item, used to keep the sheet inside
// the viewport when the click lands near the right or bottom edge.
const MENU_WIDTH = 176;
const MENU_ITEM_HEIGHT = 34;
const MENU_PADDING = 10;

export const ReportNotesContextMenu = ({ anchor, onAddNote, onClose }: MenuProps) => {
  const { t } = useTranslation();
  const sheetRef = useRef<HTMLDivElement>(null);
  const selectedText = anchor.selectedText?.trim();

  const copySelection = async () => {
    if (selectedText) {
      // Clipboard access can be refused (insecure origin, denied permission).
      // Nothing to recover: the selection is still on screen and Cmd+C works.
      try {
        await navigator.clipboard.writeText(selectedText);
      } catch {
        /* ignore */
      }
    }
    onClose();
  };

  useEffect(() => {
    // A press inside the sheet is the user choosing an item, not dismissing.
    // This guard is the whole reason the menu works: the listener is on
    // `window` in the capture phase, so without it it would close the menu
    // before React ever got to run the item's own onClick, and the item would
    // look dead.
    const onPressOutside = (event: MouseEvent) => {
      if (sheetRef.current?.contains(event.target as Node)) return;
      onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("mousedown", onPressOutside, true);
    window.addEventListener("resize", onClose);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("mousedown", onPressOutside, true);
      window.removeEventListener("resize", onClose);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  const height = MENU_PADDING + MENU_ITEM_HEIGHT * (selectedText ? 2 : 1);

  return createPortal(
    <MenuSheet
      ref={sheetRef}
      role="menu"
      style={{
        left: Math.min(anchor.x, window.innerWidth - MENU_WIDTH - 8),
        top: Math.min(anchor.y, window.innerHeight - height - 8),
      }}
      onContextMenu={(event) => event.preventDefault()}
    >
      {selectedText && (
        <MenuItem role="menuitem" onClick={copySelection}>
          <FiCopy size={12} /> {t("dueDiligence.finalReport.notes.copySelection")}
        </MenuItem>
      )}
      <MenuItem role="menuitem" onClick={onAddNote}>
        <TbNote size={13} /> {t("dueDiligence.finalReport.notes.addNote")}
      </MenuItem>
    </MenuSheet>,
    document.body,
  );
};

// ── The panel ────────────────────────────────────────────────────
// Same slide-in aside as the citation panel: one drawer vocabulary for the
// whole report.

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  background: rgba(20, 50, 59, 0.22);
  animation: ${fadeIn} 0.18s ease;
`;

const Panel = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1301;
  display: flex;
  flex-direction: column;
  width: min(430px, 94vw);
  background: ${WHITE};
  border-left: 1px solid ${BORDER_SUBTLE};
  box-shadow: -16px 0 48px rgba(20, 50, 59, 0.16);
  font-family: ${FONT_REPORT};
  animation: ${slideIn} 0.26s cubic-bezier(0.2, 0.7, 0.2, 1);
`;

const PanelHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1.625rem 1.75rem 1.125rem;
  border-bottom: 1px solid ${BORDER_SUBTLE};
`;

const PanelOverline = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${AMBER_INK};
`;

const PanelTitle = styled.div`
  margin-top: 0.375rem;
  font-size: 1.0625rem;
  font-weight: 800;
  color: ${TEXT_HEADING};
  letter-spacing: -0.02em;
  line-height: 1.3;
`;

const CloseButton = styled.button`
  flex-shrink: 0;
  width: 1.875rem;
  height: 1.875rem;
  border: 1px solid ${BORDER_SUBTLE};
  border-radius: 0.5rem;
  background: ${WHITE};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${INK.label};
`;

const NoteList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 1.25rem 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const NoteCard = styled.div`
  padding: 0.875rem 1rem;
  background: ${TINT_AMBER};
  border: 1px solid ${BORDER_TINT.amber};
  border-radius: 0.5rem;
  border-bottom-right-radius: 1.125rem;
`;

const NoteBody = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.6;
  color: ${TEXT_HEADING};
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  cursor: text;
`;

const NoteEditor = styled.textarea`
  display: block;
  width: 100%;
  min-height: 4rem;
  resize: vertical;
  padding: 0.5rem 0.625rem;
  font-family: inherit;
  font-size: 0.875rem;
  line-height: 1.6;
  color: ${TEXT_HEADING};
  background: ${WHITE};
  border: 1px solid ${BORDER_TINT.amber};
  border-radius: 0.4rem;
  outline: none;
`;

const NoteEditFoot = styled.div`
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
`;

const GhostButton = styled.button`
  padding: 0.35rem 0.75rem;
  font-family: inherit;
  font-size: 0.78rem;
  font-weight: 700;
  color: ${INK.muted};
  background: transparent;
  border: 1px solid transparent;
  border-radius: 0.4rem;
  cursor: pointer;

  &:hover {
    color: ${AMBER_INK};
  }
`;

const NoteFoot = styled.div`
  margin-top: 0.625rem;
  display: flex;
  align-items: center;
  gap: 0.625rem;
`;

const NoteMeta = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  color: ${AMBER_INK};
  opacity: 0.75;
`;

// Kept off the card until the pointer is on it: a bin sitting permanently
// beside every note invites the one action nobody wants to take by accident.
const NoteActions = styled.div`
  flex-shrink: 0;
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.125rem;
  opacity: 0;
  transition: opacity 0.14s ease;

  ${NoteCard}:hover &,
  &:focus-within {
    opacity: 1;
  }
`;

const NoteAction = styled.button<{ $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.375rem;
  height: 1.375rem;
  color: ${INK.faint};
  background: transparent;
  border: none;
  border-radius: 0.3rem;
  cursor: pointer;
  transition: color 0.14s ease;

  &:hover {
    color: ${(p) => (p.$danger ? SEMAPHORE.red.text : AMBER_INK)};
  }
`;

// Deleting is the only irreversible thing in the panel, so it asks — in the
// card itself rather than in a modal, which for two lines of text would be
// more ceremony than the note deserves.
const ConfirmRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const ConfirmPrompt = styled.span`
  flex: 1;
  min-width: 8rem;
  font-size: 0.78rem;
  font-weight: 700;
  color: ${SEMAPHORE.red.text};
`;

const ConfirmButton = styled.button`
  padding: 0.35rem 0.75rem;
  font-family: inherit;
  font-size: 0.78rem;
  font-weight: 700;
  color: ${SEMAPHORE.red.text};
  background: ${TINT_RED};
  border: 1px solid ${BORDER_TINT.red};
  border-radius: 0.4rem;
  cursor: pointer;
`;

const EmptyState = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.6;
  color: ${TEXT_PLACEHOLDER};
`;

const Composer = styled.div`
  padding: 1rem 1.75rem 1.5rem;
  border-top: 1px solid ${BORDER_SUBTLE};
`;

const NoteInput = styled.textarea`
  width: 100%;
  min-height: 5.5rem;
  resize: vertical;
  padding: 0.75rem 0.875rem;
  font-family: inherit;
  font-size: 0.875rem;
  line-height: 1.6;
  color: ${TEXT_HEADING};
  background: ${TINT_AMBER};
  border: 1px solid ${BORDER_TINT.amber};
  border-radius: 0.5rem;
  outline: none;

  &::placeholder {
    color: ${TEXT_PLACEHOLDER};
  }
`;

const ComposerFoot = styled.div`
  margin-top: 0.625rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

const CharCount = styled.span<{ $spent: boolean }>`
  font-size: 0.7rem;
  font-weight: 600;
  color: ${(p) => (p.$spent ? AMBER_INK : INK.faint)};
`;

const SaveButton = styled.button`
  padding: 0.5rem 1rem;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 700;
  color: ${AMBER_INK};
  background: ${TINT_AMBER};
  border: 1px solid ${BORDER_TINT.amber};
  border-radius: 0.5rem;
  cursor: pointer;

  &:disabled {
    cursor: default;
    color: ${INK.faint};
    background: ${WHITE};
    border-color: ${BORDER_SUBTLE};
  }
`;

interface NoteProps {
  note: DueDiligenceReportNote;
  onRewrite: (content: string) => Promise<boolean>;
  onRemove: () => Promise<boolean>;
}

const Note = ({ note, onRewrite, onRemove }: NoteProps) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const trimmed = draft?.trim() ?? "";

  const startEditing = () => {
    setConfirmingDelete(false);
    setDraft(note.content);
  };

  const save = async () => {
    if (!trimmed || saving) return;
    // Nothing to send if it came back the way it went in.
    if (trimmed === note.content) return setDraft(null);
    setSaving(true);
    const saved = await onRewrite(trimmed);
    setSaving(false);
    if (saved) setDraft(null);
  };

  return (
    <NoteCard>
      {draft === null ? (
        <NoteBody onClick={startEditing}>{note.content}</NoteBody>
      ) : (
        <>
          <NoteEditor
            autoFocus
            value={draft}
            maxLength={REPORT_NOTE_MAX_LENGTH}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                // The panel closes on Escape too; while editing, the key
                // belongs to the editor and must not reach that listener.
                event.stopPropagation();
                setDraft(null);
              }
              if ((event.metaKey || event.ctrlKey) && event.key === "Enter") save();
            }}
          />
          <NoteEditFoot>
            <GhostButton onClick={() => setDraft(null)}>
              {t("dueDiligence.finalReport.notes.cancel")}
            </GhostButton>
            <SaveButton onClick={save} disabled={!trimmed || saving}>
              {saving
                ? t("dueDiligence.finalReport.notes.saving")
                : t("dueDiligence.finalReport.notes.save")}
            </SaveButton>
          </NoteEditFoot>
        </>
      )}
      {confirmingDelete ? (
        <ConfirmRow>
          <ConfirmPrompt>{t("dueDiligence.finalReport.notes.confirmDelete")}</ConfirmPrompt>
          <GhostButton onClick={() => setConfirmingDelete(false)}>
            {t("dueDiligence.finalReport.notes.cancel")}
          </GhostButton>
          <ConfirmButton onClick={onRemove}>
            {t("dueDiligence.finalReport.notes.delete")}
          </ConfirmButton>
        </ConfirmRow>
      ) : (
        <NoteFoot>
          <NoteMeta>
            {note.authorName} · {formatAbbreviatedDate(note.createdAt)}
            {note.updatedAt !== note.createdAt &&
              ` · ${t("dueDiligence.finalReport.notes.edited")}`}
          </NoteMeta>
          <NoteActions>
            <NoteAction
              onClick={startEditing}
              title={t("dueDiligence.finalReport.notes.edit")}
              aria-label={t("dueDiligence.finalReport.notes.edit")}
            >
              <FiEdit2 size={13} />
            </NoteAction>
            <NoteAction
              $danger
              onClick={() => setConfirmingDelete(true)}
              title={t("dueDiligence.finalReport.notes.delete")}
              aria-label={t("dueDiligence.finalReport.notes.delete")}
            >
              <FiTrash2 size={13} />
            </NoteAction>
          </NoteActions>
        </NoteFoot>
      )}
    </NoteCard>
  );
};

interface PanelProps {
  sectionNumber: number;
  sectionLabel: string;
  sectionTitle: string;
  notes: DueDiligenceReportNote[];
  onAdd: (content: string) => Promise<boolean>;
  onRewrite: (noteId: string, content: string) => Promise<boolean>;
  onRemove: (noteId: string) => Promise<boolean>;
  onClose: () => void;
}

export const ReportNotesPanel = ({
  sectionNumber,
  sectionLabel,
  sectionTitle,
  notes,
  onAdd,
  onRewrite,
  onRemove,
  onClose,
}: PanelProps) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Opening the marker of a section with nothing in it is already the intent
  // to write, so the cursor goes straight into the composer.
  useEffect(() => {
    if (notes.length === 0) inputRef.current?.focus();
    // Only on open: refocusing on every save would fight the user's own cursor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionNumber]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const trimmed = draft.trim();

  const save = async () => {
    if (!trimmed || saving) return;
    setSaving(true);
    const saved = await onAdd(trimmed);
    setSaving(false);
    if (saved) {
      setDraft("");
      inputRef.current?.focus();
    }
  };

  return (
    <>
      <Overlay onClick={onClose} />
      <Panel role="dialog" aria-label={t("dueDiligence.finalReport.notes.panelTitle")}>
        <PanelHead>
          <div>
            <PanelOverline>
              <TbNote size={13} /> {t("dueDiligence.finalReport.notes.panelTitle")}
            </PanelOverline>
            <PanelTitle>
              {sectionLabel} · {sectionTitle}
            </PanelTitle>
          </div>
          <CloseButton onClick={onClose} aria-label={t("dueDiligence.finalReport.notes.close")}>
            <FiX size={15} />
          </CloseButton>
        </PanelHead>

        <NoteList>
          {notes.length === 0 ? (
            <EmptyState>{t("dueDiligence.finalReport.notes.empty")}</EmptyState>
          ) : (
            notes.map((note) => (
              <Note
                key={note.id}
                note={note}
                onRewrite={(content) => onRewrite(note.id, content)}
                onRemove={() => onRemove(note.id)}
              />
            ))
          )}
        </NoteList>

        <Composer>
          <NoteInput
            ref={inputRef}
            value={draft}
            maxLength={REPORT_NOTE_MAX_LENGTH}
            placeholder={t("dueDiligence.finalReport.notes.placeholder")}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if ((event.metaKey || event.ctrlKey) && event.key === "Enter") save();
            }}
          />
          <ComposerFoot>
            <CharCount $spent={draft.length > REPORT_NOTE_MAX_LENGTH - 200}>
              {draft.length} / {REPORT_NOTE_MAX_LENGTH}
            </CharCount>
            <SaveButton onClick={save} disabled={!trimmed || saving}>
              {saving
                ? t("dueDiligence.finalReport.notes.saving")
                : t("dueDiligence.finalReport.notes.save")}
            </SaveButton>
          </ComposerFoot>
        </Composer>
      </Panel>
    </>
  );
};
